import { useQueries } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import {
  type AchTransferBatch,
  type ListApprovalsResponse,
  type SwiftTransfer,
  type TransferReviewBatch,
  type TransferReviewBatchDecision,
  type UnifiedTransfer,
  approvalsListApprovals,
  bankAccountGetBankAccountOptions,
  counterpartyGetCounterpartyOptions,
  transferAchBatchGetBatchOptions,
  transferAchGetTransferOptions,
  transferCheckGetCheckTransferOptions,
  transferRealtimeGetTransferOptions,
  transferSwiftGetTransferOptions,
  transferWireGetWireTransferOptions,
} from '@column/api';
import { Button, Fade, Icon, Input } from '@column/column-ui-kit';
import { DialogFooter, Label } from '@column/ui';

import {
  type ReviewQuoteTransfer,
  useInternationalWireReviewQuote,
} from '~/app/pages/Transfers/_hooks/useInternationalWireReviewQuote';
import { getInternationalWireReviewFXQuoteDetails } from '~/app/pages/Transfers/_utils/internationalWire';
import {
  type ApprovalHoldRequest,
  isApprovalHold,
  isInternationalWireApprovalBlocked,
} from '~/app/pages/Transfers/_utils/transferReview';
import { ROUTE } from '~/app/routes';
import { CopyRow, CopyRowValue, EntityLink } from '~/components/CopyRow';
import { LogoLoading } from '~/elements';
import { AnimatedButton } from '~/lib/approvals/components/AnimatedButton';
import {
  AnimatedHeight,
  ApproveButton,
  BACKGROUND_NOTICE,
  RejectButton,
  ReviewDialog,
  ReviewDialogHeader,
} from '~/lib/approvals/components/ReviewDialog';
import { useDecisionAnimation } from '~/lib/approvals/hooks/useDecisionAnimation';
import { useDelayedReveal } from '~/lib/approvals/hooks/useDelayedReveal';
import { useReviewKeyboardShortcuts } from '~/lib/approvals/hooks/useReviewKeyboardShortcuts';
import { useReviewQueue } from '~/lib/approvals/hooks/useReviewQueue';
import { useReviewTransition } from '~/lib/approvals/hooks/useReviewTransition';
import { useNavigate } from '~/lib/navigation';
import { TransferHeader } from '~/lib/transfers';
import { getBatchNetAmount, getTransferURL, isBatchTransfer } from '~/lib/transfers/utils';
import { TransferEntryType, convertTransferAmount } from '~/repositories';
import { useSessionStore } from '~/stores/Session';
import { Card, Flex, Text } from '~/styles';
import { TransferReviewDecision } from '~/typings/enum';

import { ReviewBatchSummary } from './ReviewBatchSummary';

export type ReviewTransferFormValues = ApprovalHoldRequest & { fx_quote_id?: string };

const ReviewQuoteFieldWrapper = styled(CopyRowValue)`
  display: flex;
  align-self: stretch;
  align-items: center;
  padding: 0 4px 0 16px;

  @container copy-row (min-width: ${({ theme }) => theme.containerBreakpoints.tablet}) {
    padding: 0 8px 0 36px;
  }
`;

export interface ReviewListQuery {
  show_batches?: boolean;
}

interface ReviewSingleDialogProps {
  listQuery?: ReviewListQuery;
  transfers: UnifiedTransfer[];
  transferStartIndex: number;
  currentIndex: number;
  totalTransfers: number;
  isOpen: boolean;
  onClose: () => void;
  onCurrentTransferChange?: (transferId: string | undefined) => void;
  unavailableIds?: ReadonlySet<string>;
  onSubmit: (
    transferId: string,
    transferType: TransferEntryType,
    transferStatus: string,
    request: ReviewTransferFormValues
  ) => Promise<TransferReviewBatch | void>;
}

interface BatchReviewState {
  id: string;
  decision: TransferReviewBatchDecision;
  count: number;
}

const MODAL_PAGE_SIZE = 10;
const extractTransfers = (page: ListApprovalsResponse | undefined): UnifiedTransfer[] =>
  page?.approvals.map((entry) => entry.transfer) ?? [];

const getCreatedByName = (createdBy: unknown): string | undefined => {
  if (!createdBy || typeof createdBy !== 'object') return undefined;
  const { name } = createdBy as { name?: unknown };
  return typeof name === 'string' ? name : undefined;
};

const getCheckPayeeName = (checkTransfer: unknown): string | undefined => {
  if (!checkTransfer || typeof checkTransfer !== 'object') return undefined;
  const { payee_name: payeeName } = checkTransfer as { payee_name?: unknown };
  return typeof payeeName === 'string' ? payeeName : undefined;
};

const ViewTransferButton = styled(Button)`
  padding: 0;
`;

const LoadingContainer = styled.div<{ show: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 32px;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity 0.2s ease;
  pointer-events: none;
`;

const StyledCard = styled(Card)`
  .transfer-page-header {
    padding: 16px 16px 6px;
  }
`;

const getTransferBankAccountId = (transfer: UnifiedTransfer, detail: unknown): string | undefined =>
  transfer.sender_internal_account?.bank_account_id ??
  (detail as AchTransferBatch | undefined)?.import_config?.nacha?.bank_account_id ??
  undefined;

export const ReviewSingleDialog: React.FC<ReviewSingleDialogProps> = ({
  listQuery,
  transfers: transfersProp,
  transferStartIndex,
  currentIndex: currentIndexProp,
  totalTransfers: totalTransfersProp,
  isOpen,
  onClose,
  onCurrentTransferChange,
  unavailableIds,
  onSubmit,
}) => {
  const navigate = useNavigate();
  const isSandbox = useSessionStore((state) => state.isSandbox);
  const [isSubmitting, setIsSubmitting] = useState<TransferReviewDecision>();
  const [batchReview, setBatchReview] = useState<BatchReviewState>();
  const [isWide, setIsWide] = useState(false);
  const formMethods = useForm<ReviewTransferFormValues>();
  const comment = formMethods.watch('comment');
  const commentFocused = useRef(false);

  const {
    items: transfers,
    setItems: setTransfers,
    currentIndex,
    setCurrentIndex,
    removeById,
  } = useReviewQueue<UnifiedTransfer | undefined>();
  const { showContent, setShowContent, slideDirection, setSlideDirection, slide } = useReviewTransition();
  const { approveAnimation, rejectAnimation, play: playAnimation, reset: resetAnimation } = useDecisionAnimation();
  const [isFetchingMoreTransfers, setIsFetchingMoreTransfers] = useState(false);
  const adjacentTransfers = useMemo(() => {
    const seen = new Set<string>();
    return [transfers[currentIndex], transfers[currentIndex - 1], transfers[currentIndex + 1]].filter(
      (transfer): transfer is UnifiedTransfer => {
        if (!transfer || seen.has(transfer.id)) return false;
        seen.add(transfer.id);
        return true;
      }
    );
  }, [currentIndex, transfers]);
  const transferDetailQueries = useMemo(
    () =>
      adjacentTransfers
        .map((transfer) => {
          if (transfer.type === 'ach_credit' || transfer.type === 'ach_debit') {
            return {
              id: transfer.id,
              options: transferAchGetTransferOptions({ path: { ach_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'wire') {
            return {
              id: transfer.id,
              options: transferWireGetWireTransferOptions({ path: { wire_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'check_debit') {
            return {
              id: transfer.id,
              options: transferCheckGetCheckTransferOptions({ path: { check_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'realtime') {
            return {
              id: transfer.id,
              options: transferRealtimeGetTransferOptions({ path: { realtime_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'swift') {
            return {
              id: transfer.id,
              options: transferSwiftGetTransferOptions({ path: { swift_transfer_id: transfer.id } }),
            };
          }
          if (transfer.type === 'ach_batch') {
            return {
              id: transfer.id,
              options: transferAchBatchGetBatchOptions({ path: { ach_transfer_batch_id: transfer.id } }),
            };
          }
          return undefined;
        })
        .filter((query): query is NonNullable<typeof query> => Boolean(query)),
    [adjacentTransfers]
  );
  const transferDetailResults = useQueries({
    queries: transferDetailQueries.map(({ options }) => ({
      ...options,
      enabled: isOpen,
    })),
  });
  const transferDetails = useMemo(() => {
    const next: Record<string, { data: unknown; isLoading: boolean }> = {};
    transferDetailQueries.forEach(({ id }, index) => {
      const result = transferDetailResults[index];
      next[id] = { data: result?.data, isLoading: Boolean(result?.isLoading || result?.isFetching) };
    });
    return next;
  }, [transferDetailQueries, transferDetailResults]);
  const bankAccountIds = useMemo(() => {
    const seen = new Set<string>();
    adjacentTransfers.forEach((transfer) => {
      const bankAccountId = getTransferBankAccountId(transfer, transferDetails[transfer.id]?.data);
      if (bankAccountId) seen.add(bankAccountId);
    });
    return Array.from(seen);
  }, [adjacentTransfers, transferDetails]);
  const bankAccountResults = useQueries({
    queries: bankAccountIds.map((bankAccountId) => ({
      ...bankAccountGetBankAccountOptions({ path: { bank_account_id: bankAccountId } }),
      enabled: isOpen,
    })),
  });
  const counterpartyIds = useMemo(() => {
    const seen = new Set<string>();
    adjacentTransfers.forEach((transfer) => {
      const counterpartyId = transfer.external_destination?.counterparty_id;
      if (counterpartyId) seen.add(counterpartyId);
    });
    return Array.from(seen);
  }, [adjacentTransfers]);
  const counterpartyResults = useQueries({
    queries: counterpartyIds.map((counterpartyId) => ({
      ...counterpartyGetCounterpartyOptions({ path: { counterparty_id: counterpartyId } }),
      enabled: isOpen,
    })),
  });
  const creators = useMemo(() => {
    const next: Record<string, string | undefined> = {};
    // Seed from the approvals row so there's a name before the detail query resolves.
    adjacentTransfers.forEach((transfer) => {
      next[transfer.id] = getCreatedByName(transfer.created_by);
    });
    transferDetailQueries.forEach(({ id }, index) => {
      const detail = transferDetailResults[index]?.data;
      const detailCreator = detail && getCreatedByName((detail as { created_by?: unknown }).created_by);
      if (detailCreator) next[id] = detailCreator;
    });
    return next;
  }, [adjacentTransfers, transferDetailQueries, transferDetailResults]);
  const checkPayees = useMemo(() => {
    const next: Record<string, string | undefined> = {};
    adjacentTransfers.forEach((transfer) => {
      if (transfer.type === 'check_debit') {
        next[transfer.id] = transfer.external_destination?.receiver_name;
      }
    });
    transferDetailQueries.forEach(({ id }, index) => {
      const payeeName = getCheckPayeeName(transferDetailResults[index]?.data);
      if (payeeName) next[id] = payeeName;
    });
    return next;
  }, [adjacentTransfers, transferDetailQueries, transferDetailResults]);
  const bankAccounts = useMemo(() => {
    const next: Record<string, (typeof bankAccountResults)[number]['data']> = {};
    bankAccountIds.forEach((id, index) => {
      const bankAccount = bankAccountResults[index]?.data;
      if (bankAccount) next[id] = bankAccount;
    });
    return next;
  }, [bankAccountIds, bankAccountResults]);
  const bankAccountsLoading = useMemo(() => {
    const next: Record<string, boolean> = {};
    bankAccountIds.forEach((id, index) => {
      const result = bankAccountResults[index];
      next[id] = Boolean(result?.isLoading || result?.isFetching);
    });
    return next;
  }, [bankAccountIds, bankAccountResults]);
  const counterparties = useMemo(() => {
    const next: Record<string, (typeof counterpartyResults)[number]['data']> = {};
    counterpartyIds.forEach((id, index) => {
      const counterparty = counterpartyResults[index]?.data;
      if (counterparty) next[id] = counterparty;
    });
    return next;
  }, [counterpartyIds, counterpartyResults]);
  const counterpartiesLoading = useMemo(() => {
    const next: Record<string, boolean> = {};
    counterpartyIds.forEach((id, index) => {
      const result = counterpartyResults[index];
      next[id] = Boolean(result?.isLoading || result?.isFetching);
    });
    return next;
  }, [counterpartyIds, counterpartyResults]);
  const currentData = useMemo(() => {
    const transfer = transfers[currentIndex];
    const bankAccountId = transfer && getTransferBankAccountId(transfer, transferDetails[transfer.id]?.data);
    const counterpartyId = transfer?.external_destination?.counterparty_id;

    return {
      bankAccount: bankAccountId ? bankAccounts[bankAccountId] : undefined,
      bankAccountId,
      counterparty: counterpartyId ? counterparties[counterpartyId] : undefined,
      counterpartyId,
      checkPayee: transfer?.id ? checkPayees[transfer.id] : undefined,
      creator: transfer?.id ? creators[transfer.id] : undefined,
      transfer,
    };
  }, [bankAccounts, checkPayees, counterparties, creators, currentIndex, transferDetails, transfers]);
  const currentTransferDetailResult = useMemo(() => {
    if (currentData.transfer?.type !== 'swift') return;
    const detailIndex = transferDetailQueries.findIndex(({ id }) => id === currentData.transfer?.id);
    return detailIndex >= 0 ? transferDetailResults[detailIndex] : undefined;
  }, [currentData.transfer, transferDetailQueries, transferDetailResults]);
  const currentSwiftTransferDetail = currentTransferDetailResult?.data as SwiftTransfer | undefined;
  const isCurrentInternationalWireApprovalBlocked = isInternationalWireApprovalBlocked(
    currentData.transfer?.type,
    currentTransferDetailResult
      ? {
          hasData: Boolean(currentSwiftTransferDetail),
          isError: currentTransferDetailResult.isError,
          isFetching: currentTransferDetailResult.isFetching,
          isLoading: currentTransferDetailResult.isLoading,
        }
      : undefined
  );
  const reviewQuoteTransfer = useMemo<ReviewQuoteTransfer | undefined>(() => {
    const transfer = currentData.transfer;
    if (transfer?.type !== 'swift') return;
    if (isApprovalHold(transfer.status)) return;
    if (isCurrentInternationalWireApprovalBlocked || !currentSwiftTransferDetail) return;
    return {
      amount: currentSwiftTransferDetail.amount,
      currencyCode: currentSwiftTransferDetail.currency_code,
      fxQuoteId: currentSwiftTransferDetail.fx_quote_id,
      id: transfer.id,
      instructedAmount: currentSwiftTransferDetail.instructed_amount ?? undefined,
      instructedCurrencyCode: currentSwiftTransferDetail.instructed_currency_code,
    };
  }, [currentData.transfer, currentSwiftTransferDetail, isCurrentInternationalWireApprovalBlocked]);
  const {
    isFXTransfer: isCurrentFXTransfer,
    isLoading: isFXQuoteLoading,
    quote: currentFXQuote,
    refreshQuote: requestFXQuote,
  } = useInternationalWireReviewQuote(reviewQuoteTransfer, isOpen);
  const isCurrentBatch = Boolean(currentData.transfer && isBatchTransfer(currentData.transfer));
  const emptyState = transfers.length === 0;
  const isLoadingAdditionalData =
    isOpen &&
    !emptyState &&
    !!currentData.transfer &&
    (Boolean(transferDetails[currentData.transfer.id]?.isLoading) ||
      (!!currentData.bankAccountId && !currentData.bankAccount && bankAccountsLoading[currentData.bankAccountId]) ||
      (!!currentData.counterpartyId && !currentData.counterparty && counterpartiesLoading[currentData.counterpartyId]));
  useEffect(() => {
    if (!isLoadingAdditionalData) {
      setShowContent(true);
    }
  }, [isLoadingAdditionalData]);
  useEffect(() => {
    if (!isOpen) return;
    commentFocused.current = false;
    setBatchReview(undefined);
    setIsWide(false);
    const arr: (UnifiedTransfer | undefined)[] = Array(totalTransfersProp).fill(undefined);
    transfersProp.forEach((transfer, i) => {
      const slot = transferStartIndex + i;
      if (slot < arr.length) arr[slot] = transfer;
    });
    setTransfers(arr);
    setCurrentIndex(currentIndexProp);
  }, [isOpen]);

  const fetchSlot = useCallback(
    async (idx: number) => {
      setIsFetchingMoreTransfers(true);
      try {
        let cursorIdx = idx - 1;
        const snapshot = transfers;
        while (cursorIdx >= 0 && !snapshot[cursorIdx]) cursorIdx--;
        const cursor = cursorIdx >= 0 ? snapshot[cursorIdx]!.id : undefined;

        const response = await approvalsListApprovals({
          query: { ...listQuery, status: 'pending', starting_after: cursor, limit: MODAL_PAGE_SIZE },
          throwOnError: true,
        });
        const fetched = extractTransfers(response.data);
        const startIdx = cursorIdx + 1;
        setTransfers((previous) => {
          const arr = previous.slice();
          fetched.forEach((transfer, i) => {
            if (startIdx + i < arr.length) arr[startIdx + i] = transfer;
          });
          return arr;
        });
      } finally {
        setIsFetchingMoreTransfers(false);
      }
    },
    [listQuery, transfers]
  );

  const handlePrevious = useCallback(() => {
    const prevIndex = currentIndex - 1;
    if (prevIndex < 0 || !transfers[prevIndex]) return;

    commentFocused.current = false;
    slide('down', () => {
      formMethods.reset();
      setCurrentIndex(prevIndex);
    });
  }, [currentIndex, formMethods, setCurrentIndex, slide, transfers]);

  const handleNext = useCallback(() => {
    const nextIndex = currentIndex + 1;
    if (nextIndex >= transfers.length) return;

    commentFocused.current = false;
    slide('up', async () => {
      if (!transfers[nextIndex]) await fetchSlot(nextIndex);
      formMethods.reset();
      setCurrentIndex(nextIndex);
    });
  }, [currentIndex, fetchSlot, formMethods, setCurrentIndex, slide, transfers]);

  useEffect(() => {
    if (!isOpen) return;
    onCurrentTransferChange?.(currentData.transfer?.id);
  }, [currentData.transfer?.id, isOpen, onCurrentTransferChange]);
  const isBodyLoading = !currentData.transfer || isLoadingAdditionalData || isFetchingMoreTransfers;
  const { isVisible } = useDelayedReveal(isOpen, isBodyLoading);
  const isCurrentUnavailable = Boolean(currentData.transfer && unavailableIds?.has(currentData.transfer.id));
  const isActionDisabled = emptyState || isBodyLoading || isCurrentUnavailable;

  const handleSubmit = useCallback(
    async (decision: TransferReviewDecision) => {
      if (!currentData.transfer) return;
      const transferId = currentData.transfer.id;
      if (decision === TransferReviewDecision.Approved && isCurrentInternationalWireApprovalBlocked) return;
      if (decision === TransferReviewDecision.Approved && isCurrentFXTransfer && !currentFXQuote) return;

      setIsSubmitting(decision);

      try {
        const reviewBatch = await onSubmit(
          currentData.transfer.id,
          currentData.transfer.type as TransferEntryType,
          currentData.transfer.status,
          {
            comment,
            decision,
            fx_quote_id:
              decision === TransferReviewDecision.Approved && isCurrentFXTransfer ? currentFXQuote?.id : undefined,
          }
        );

        if (reviewBatch?.id) {
          setBatchReview({
            id: reviewBatch.id,
            decision: reviewBatch.decision,
            count: currentData.transfer.batch_transfer_count ?? reviewBatch.total_count,
          });
          return;
        }

        if (decision === TransferReviewDecision.Approved) {
          await playAnimation('approve');
        } else if (decision === TransferReviewDecision.Rejected) {
          await playAnimation('reject');
        }
      } catch (error) {
        if (
          decision === TransferReviewDecision.Approved &&
          isCurrentFXTransfer &&
          (error as { code?: string })?.code === 'fx_quote_expired'
        ) {
          await requestFXQuote();
          return;
        }
        throw error;
      } finally {
        setIsSubmitting(undefined);
        resetAnimation();
      }

      removeById(transferId);
      formMethods.reset();
    },
    [
      comment,
      currentData,
      currentFXQuote,
      formMethods,
      isCurrentFXTransfer,
      isCurrentInternationalWireApprovalBlocked,
      onSubmit,
      playAnimation,
      removeById,
      requestFXQuote,
      resetAnimation,
    ]
  );

  const dismissBatchReview = useCallback(
    (direction: 'stay' | 'previous' = 'stay') => {
      setBatchReview(undefined);
      setIsWide(false);
      if (currentData.transfer) removeById(currentData.transfer.id);
      if (direction === 'previous') setCurrentIndex(Math.max(currentIndex - 1, 0));
      formMethods.reset();
    },
    [currentData.transfer, currentIndex, formMethods, removeById, setCurrentIndex]
  );

  const continueAfterBatchReview = useCallback(() => dismissBatchReview('stay'), [dismissBatchReview]);

  const goPrevious = useCallback(() => {
    if (batchReview) {
      setSlideDirection('down');
      dismissBatchReview('previous');
      return;
    }
    handlePrevious();
  }, [batchReview, dismissBatchReview, handlePrevious]);

  const goNext = useCallback(() => {
    if (batchReview) {
      setSlideDirection('up');
      continueAfterBatchReview();
      return;
    }
    handleNext();
  }, [batchReview, continueAfterBatchReview, handleNext]);

  const approve = useCallback(() => handleSubmit(TransferReviewDecision.Approved), [handleSubmit]);
  const reject = useCallback(() => handleSubmit(TransferReviewDecision.Rejected), [handleSubmit]);

  useReviewKeyboardShortcuts({
    enabled: isVisible,
    isBlocked: isLoadingAdditionalData || !!isSubmitting || isFetchingMoreTransfers,
    isContinueMode: !!batchReview,
    canDecide: !isActionDisabled,
    pauseNavigationRef: commentFocused,
    onApprove: approve,
    onReject: reject,
    onNext: goNext,
    onPrevious: goPrevious,
  });

  return (
    <ReviewDialog
      isOpen={isVisible}
      onClose={onClose}
      showCloseButton={false}
      isWide={isWide}
      className="pt-[22px] sm:max-w-[640px]"
    >
      <ReviewDialogHeader
        title="Review Transfer"
        description={batchReview ? BACKGROUND_NOTICE : 'Approve or reject transfers pending approval.'}
        isDescriptionVisible={!!batchReview}
        showCounter={!emptyState}
        slideDirection={slideDirection}
        current={currentIndex + 1}
        total={transfers.length}
        onPrevious={goPrevious}
        onNext={goNext}
      />

      <AnimatedHeight freeze={isBodyLoading || !showContent}>
        {batchReview ? (
          <div className="flex min-w-0 flex-col justify-center">
            <ReviewBatchSummary
              decision={batchReview.decision}
              requestedCount={batchReview.count}
              reviewBatchId={batchReview.id}
              onHasAttentionChange={setIsWide}
            />
          </div>
        ) : emptyState ? (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-center">
            <Text size={18} weight="medium" color="foreground">
              All caught up
            </Text>
            <Text size={14} color="light">
              There are no more transfers to review
            </Text>
          </div>
        ) : (
          <div className="relative">
            <LoadingContainer show={isBodyLoading}>
              <LogoLoading />
            </LoadingContainer>
            <Fade
              key={currentIndex}
              show={!isLoadingAdditionalData && showContent}
              distance={16}
              direction={slideDirection === 'up' ? 'right' : 'left'}
            >
              <div className="grid gap-6">
                <StyledCard>
                  <TransferHeader
                    className="transfer-page-header"
                    metaAction={
                      <ViewTransferButton
                        icon={<Icon.ChevronRightLarge />}
                        iconRight
                        variant="subtle"
                        onClick={() =>
                          currentData.transfer &&
                          navigate(
                            getTransferURL(currentData.transfer.id, currentData.transfer.type as TransferEntryType)
                          )
                        }
                      >
                        View
                      </ViewTransferButton>
                    }
                    amount={
                      isCurrentBatch
                        ? Math.abs(currentData.transfer?.amount ?? 0)
                        : (currentFXQuote?.sellAmount ?? currentData.transfer?.amount ?? 0)
                    }
                    createdAt={currentData.transfer?.created_at ?? undefined}
                    currencyCode={currentFXQuote?.sellCurrencyCode ?? currentData.transfer?.currency_code}
                    direction={{ type: 'outgoing' }}
                    isAdding={
                      isCurrentBatch && currentData.transfer
                        ? getBatchNetAmount(currentData.transfer) > 0
                        : currentData.transfer?.type
                          ? convertTransferAmount(
                              currentData.transfer.type as TransferEntryType,
                              currentData.transfer.is_incoming
                            ) > 0
                          : undefined
                    }
                    settledCurrencyAmount={currentSwiftTransferDetail?.settled_amount ?? undefined}
                    settledCurrencyCode={currentSwiftTransferDetail?.settled_currency_code}
                    transferType={currentData.transfer ? (currentData.transfer.type as TransferEntryType) : 'wire'}
                  />
                  {isCurrentBatch ? (
                    <>
                      {currentData.bankAccountId && (
                        <CopyRow
                          label="Account"
                          value={
                            <EntityLink
                              label={currentData.bankAccount?.description || currentData.bankAccountId}
                              onClick={() => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${currentData.bankAccountId}`)}
                            />
                          }
                        />
                      )}
                      <CopyRow label="Transfers" value={String(currentData.transfer?.batch_transfer_count ?? '-')} />
                      {currentData.creator && <CopyRow label="Created By" value={currentData.creator} />}
                      <CopyRow label="Description" value={currentData.transfer?.description || '-'} />
                    </>
                  ) : (
                    <>
                      <CopyRow
                        label="Account"
                        value={
                          currentData.bankAccountId ? (
                            <EntityLink
                              label={currentData.bankAccount?.description || currentData.bankAccountId}
                              onClick={() => navigate(`${ROUTE.BANK_ACCOUNTS}/edit/${currentData.bankAccountId}`)}
                            />
                          ) : (
                            '-'
                          )
                        }
                      />
                      <CopyRow
                        label={currentData.transfer?.type === 'check_debit' ? 'Payee' : 'Counterparty'}
                        value={
                          currentData.counterpartyId && !currentData.checkPayee ? (
                            <EntityLink
                              label={currentData.counterparty?.name || currentData.counterpartyId}
                              onClick={() => navigate(`${ROUTE.COUNTERPARTIES}/edit/${currentData.counterpartyId}`)}
                            />
                          ) : (
                            currentData.checkPayee || currentData.counterparty?.name || '-'
                          )
                        }
                      />
                      <CopyRow label="Created By" value={currentData.creator || '-'} />
                      <CopyRow label="Description" value={currentData.transfer?.description || '-'} />
                    </>
                  )}
                  {isCurrentFXTransfer &&
                    getInternationalWireReviewFXQuoteDetails({
                      context: 'copy-row',
                      isLoading: isFXQuoteLoading,
                      isSandbox: !!isSandbox,
                      onRetry: requestFXQuote,
                      quote: currentFXQuote,
                    }).map(({ label, value }) => (
                      <CopyRow
                        key={label}
                        label={label}
                        value={
                          typeof value === 'string' ? value : <ReviewQuoteFieldWrapper>{value}</ReviewQuoteFieldWrapper>
                        }
                      />
                    ))}
                </StyledCard>
                <div className="grid gap-2">
                  <Label>Comment</Label>
                  <Input
                    value={comment ?? ''}
                    isDisabled={!!isSubmitting}
                    placeholder="Add a comment..."
                    onChange={(value) => formMethods.setValue('comment', value)}
                    onFocus={() => (commentFocused.current = true)}
                    onBlur={() => (commentFocused.current = false)}
                  />
                </div>
              </div>
            </Fade>
          </div>
        )}
      </AnimatedHeight>

      <DialogFooter className="flex-row items-center justify-between sm:justify-between">
        {batchReview ? (
          <>
            <AnimatedButton onClick={onClose} variant="secondary">
              Close
            </AnimatedButton>
            <Button size="small" onClick={continueAfterBatchReview}>
              Continue
              <Icon.ChevronRightLarge />
            </Button>
          </>
        ) : (
          <>
            <AnimatedButton onClick={onClose} variant="secondary">
              Cancel
            </AnimatedButton>
            <Flex alignItems="center" flexDirection="row" gap={8} justifyContent="flex-end">
              <RejectButton
                onClick={reject}
                isLoading={isSubmitting === TransferReviewDecision.Rejected && !rejectAnimation}
                isDisabled={isActionDisabled || isSubmitting === TransferReviewDecision.Approved}
                animationState={rejectAnimation}
              >
                Reject
              </RejectButton>
              <ApproveButton
                onClick={approve}
                isLoading={isSubmitting === TransferReviewDecision.Approved && !approveAnimation}
                isDisabled={
                  isActionDisabled ||
                  isSubmitting === TransferReviewDecision.Rejected ||
                  isCurrentInternationalWireApprovalBlocked ||
                  (isCurrentFXTransfer && (isFXQuoteLoading || !currentFXQuote))
                }
                animationState={approveAnimation}
              >
                Approve
              </ApproveButton>
            </Flex>
          </>
        )}
      </DialogFooter>
    </ReviewDialog>
  );
};
